import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Sun, Moon, Check, Mail, Globe } from "lucide-react";

import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PortalShell } from "@/components/account/PortalShell";
import { detectTz, fmtLocal, COMMON_TZS } from "@/lib/time";

const OPTIONS = [
    {
        key: "light",
        label: "Light",
        Icon: Sun,
        blurb: "White canvas · navy + orange accents.",
        swatch: ["#FFFFFF", "#F5F7FB", "#0B1E3E", "#FF5A00"],
    },
    {
        key: "dark",
        label: "Dark",
        Icon: Moon,
        blurb: "Deep navy · signature NOXN palette.",
        swatch: ["#0F1E3B", "#1B2E55", "#FFFFFF", "#FF5A00"],
    },
];

const TimezoneCard = () => {
    const { user, updateTimezone } = useAuth();
    const detected = detectTz();
    const [saving, setSaving] = useState(false);
    const current = user?.timezone || detected;
    const options = COMMON_TZS.includes(detected) ? COMMON_TZS : [detected, ...COMMON_TZS];
    const pick = async (tz) => {
        if (tz === current) return;
        setSaving(true);
        try {
            await updateTimezone(tz);
            toast.success(`Time zone set to ${tz}.`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not save time zone.");
        } finally { setSaving(false); }
    };
    return (
        <section className="mt-6 border border-white/10 bg-[#1B2E55]/50 p-6" data-testid="settings-timezone-card">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                    <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#FF5A00]">/ Time zone</div>
                    <div className="mt-1 text-white text-lg font-display font-black tracking-tighter inline-flex items-center gap-2">
                        <Globe size={16} className="text-[#FF5A00]" /> Local time
                    </div>
                </div>
                <div className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-500">
                    Now · <span className="text-white" data-testid="settings-timezone-now">{fmtLocal(new Date().toISOString(), current)}</span>
                </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
                <select value={current} disabled={saving} onChange={(e) => pick(e.target.value)} data-testid="settings-timezone-select"
                    className="bg-[#0F1E3B] border border-white/10 text-white px-3 py-2.5 text-sm min-w-[240px]">
                    {options.map((tz) => (
                        <option key={tz} value={tz}>{tz}{tz === detected ? " (auto-detected)" : ""}</option>
                    ))}
                </select>
                {!user?.timezone && (
                    <span className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-500" data-testid="settings-timezone-autodetected">
                        Auto-detected from this browser
                    </span>
                )}
            </div>
            <p className="mt-4 text-[10px] font-mono-tech uppercase tracking-[0.3em] text-slate-500">
                All times are stored in UTC and shown in your zone. Campaign schedules run on each screen's local time zone.
            </p>
        </section>
    );
};

export default function Settings() {
    usePageMeta({ title: "Settings · NOXN CRM", description: "Manage your NOXN account preferences." });
    const { user } = useAuth();
    const { theme, setTheme } = useTheme();

    const pick = async (next) => {
        if (next === theme) return;
        await setTheme(next);
        toast.success(`Switched to ${next} mode.`);
    };

    return (
        <PortalShell title="Settings" mainClassName="p-0">
        <div className="min-h-screen w-full relative bg-[#0F1E3B]">
            <div className="absolute inset-0 -z-10 grid-noise opacity-25" />

            <main className="mx-auto max-w-5xl px-6 lg:px-10 py-12">
                <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.7, ease: [0.16,1,0.3,1] }}>
                    <div className="font-mono-tech text-[11px] uppercase tracking-[0.35em] text-[#FF5A00] mb-4 flex items-center gap-3">
                        <span className="inline-block w-6 h-px bg-[#FF5A00]" /> Account · Settings
                    </div>
                    <h1 className="font-display font-black tracking-tighter text-white text-4xl sm:text-5xl leading-[0.95]">
                        Make it yours.
                    </h1>
                    <p className="mt-4 text-slate-400 text-sm max-w-xl">
                        Preferences save to your account so every device you sign in on picks them up automatically.
                    </p>
                </motion.div>

                {/* Account card */}
                <section className="mt-10 border border-white/10 bg-[#1B2E55]/50 p-6">
                    <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-3">/ Account</div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                        <div>
                            <div className="text-[10px] font-mono-tech uppercase tracking-[0.3em] text-slate-500">Email</div>
                            <div className="text-white mt-1 truncate" data-testid="settings-account-email">{user?.email || "—"}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-mono-tech uppercase tracking-[0.3em] text-slate-500">Phone</div>
                            <div className="text-white mt-1 truncate" data-testid="settings-account-phone">{user?.phone || "—"}</div>
                        </div>
                        <div>
                            <div className="text-[10px] font-mono-tech uppercase tracking-[0.3em] text-slate-500">Role</div>
                            <div className="text-white mt-1 uppercase tracking-widest text-xs font-mono-tech" data-testid="settings-account-role">{user?.role}</div>
                        </div>
                    </div>
                </section>

                {/* Theme card */}
                <section className="mt-6 border border-white/10 bg-[#1B2E55]/50 p-6" data-testid="settings-theme-card">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#FF5A00]">/ Appearance</div>
                            <div className="mt-1 text-white text-lg font-display font-black tracking-tighter">Theme</div>
                        </div>
                        <div className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-500">
                            Current · <span className="text-white" data-testid="settings-current-theme">{theme}</span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {OPTIONS.map(({ key, label, Icon, blurb, swatch }) => {
                            const active = theme === key;
                            return (
                                <button
                                    key={key}
                                    type="button"
                                    onClick={() => pick(key)}
                                    data-testid={`settings-theme-option-${key}`}
                                    className={`text-left border p-5 transition-colors ${
                                        active
                                            ? "border-[#FF5A00] bg-[#FF5A00]/10"
                                            : "border-white/10 hover:border-white/30 bg-[#0F1E3B]/40"
                                    }`}
                                >
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="inline-flex items-center gap-2 text-white">
                                            <Icon size={16} className="text-[#FF5A00]" />
                                            <span className="font-display font-black tracking-tighter text-lg">{label}</span>
                                        </div>
                                        {active && (
                                            <span className="inline-flex items-center gap-1 text-[10px] font-mono-tech uppercase tracking-widest text-[#FF5A00]">
                                                <Check size={12} /> Active
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-slate-400">{blurb}</p>
                                    <div className="mt-4 flex gap-1.5">
                                        {swatch.map((s, i) => (
                                            <span key={i} className="w-6 h-6 border border-white/10" style={{ backgroundColor: s }} />
                                        ))}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                    <p className="mt-4 text-[10px] font-mono-tech uppercase tracking-[0.3em] text-slate-500">
                        Light mode applies to dashboard pages only — the public landing page keeps its NOXN black.
                    </p>
                </section>

                <TimezoneCard />

                {user?.role === "admin" && (
                    <section className="mt-6 border border-white/10 bg-[#1B2E55]/50 p-6" data-testid="settings-admin-card">
                        <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#FF5A00] mb-2">/ Admin</div>
                        <div className="text-white text-sm mb-4">Manage the automated emails NOXN sends to clients.</div>
                        <Link to="/dashboard/settings/emails" data-testid="settings-emails-link"
                            className="inline-flex items-center gap-2 bg-[#FF5A00] hover:bg-[#E65200] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-widest transition-colors">
                            <Mail size={12} /> Email templates
                        </Link>
                    </section>
                )}
            </main>
        </div>
        </PortalShell>
    );
}
